import DOMPurify from "dompurify";
import { useEffect, useEffectEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

// Extract sanitize function for cleaner code
const { sanitize } = DOMPurify;

const InteractiveRedirector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { surveyId } = useParams();

  const handleRedirect = useEffectEvent(() => {
    if (!surveyId) {
      navigate("/", { replace: true });
      return;
    }

    // Sanitize surveyId to prevent XSS
    const sanitizedSurveyId = sanitize(surveyId, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });

    if (!sanitizedSurveyId) {
      navigate("/", { replace: true });
      return;
    }

    // Get the new interactive domain from environment variables
    const newInteractiveDomain =
      import.meta.env.VITE_INTERACTIVE_WEB_APP_ROOT_URL ||
      window.location.origin;

    // Extract path after "/interactive" - simplified logic
    const currentPath = location.pathname;
    const interactiveIndex = currentPath.indexOf("/interactive");
    const rawRemainingPath =
      interactiveIndex !== -1
        ? currentPath.substring(interactiveIndex + "/interactive".length)
        : "";

    // Sanitize the remaining path
    const sanitizedRemainingPath = sanitize(rawRemainingPath, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });

    // Construct the path with fallback to /login
    const finalPath = sanitizedRemainingPath || "/login";

    // Sanitize query parameters and hash
    const sanitizedSearch = sanitize(location.search, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });

    const sanitizedHash = sanitize(location.hash, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });

    // Build and validate the final URL using the URL API to prevent open redirects
    let safeUrl: URL;
    try {
      const domainUrl = new URL(newInteractiveDomain);
      if (domainUrl.protocol !== "https:" && domainUrl.protocol !== "http:") {
        throw new Error("Invalid interactive domain protocol");
      }
      safeUrl = new URL(
        `/interactive/${sanitizedSurveyId}${finalPath}`,
        domainUrl.origin,
      );
      if (sanitizedSearch) safeUrl.search = sanitizedSearch;
      if (sanitizedHash) safeUrl.hash = sanitizedHash;
    } catch {
      navigate("/", { replace: true });
      return;
    }

    // Redirect to the validated URL
    window.location.assign(safeUrl.href);
  });

  useEffect(() => {
    handleRedirect();
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Redirecting to interactive survey...</p>
      </div>
    </div>
  );
};

export default InteractiveRedirector;
